import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, getKnownAccounts, removeKnownAccount } from "@/lib/AuthProvider";
import { resolveMediaUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Check, X, LogIn } from "lucide-react";

export default function AccountSwitcher({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    if (open) setAccounts(getKnownAccounts());
  }, [open]);

  const switchTo = async (acc) => {
    if (acc.user_id === user?.user_id) { onOpenChange(false); return; }
    await logout();
    onOpenChange(false);
    navigate(`/login?prefill=${encodeURIComponent(acc.email)}`);
  };

  const removeAcc = (e, acc) => {
    e.stopPropagation();
    removeKnownAccount(acc.user_id);
    setAccounts(getKnownAccounts());
  };

  const addAccount = async () => {
    await logout();
    onOpenChange(false);
    navigate("/login?multi=1");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="account-switcher">
        <DialogHeader>
          <p className="eyebrow">Stylebay · Accounts</p>
          <DialogTitle className="font-serif text-3xl tracking-tight">Switch account</DialogTitle>
        </DialogHeader>

        <ul className="divide-y divide-border border-y border-border -mx-6">
          {accounts.length === 0 && (
            <li className="px-6 py-6 text-sm text-muted-foreground">No saved accounts yet.</li>
          )}
          {accounts.map((acc) => {
            const isCurrent = acc.user_id === user?.user_id;
            return (
              <li key={acc.user_id}>
                <button
                  type="button"
                  data-testid={`switcher-account-${acc.user_id}`}
                  onClick={() => switchTo(acc)}
                  className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="h-11 w-11 border border-border overflow-hidden shrink-0">
                    {acc.picture
                      ? <img src={resolveMediaUrl(acc.picture)} alt="" className="h-full w-full object-cover" />
                      : <span className="text-base font-medium flex items-center justify-center h-full">{(acc.name || acc.email)?.[0]?.toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{acc.name || acc.email}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {acc.username ? `@${acc.username}` : acc.email}
                    </p>
                  </div>
                  {isCurrent ? (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-accent inline-flex items-center gap-1">
                      <Check size={12} /> Current
                    </span>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={(e) => removeAcc(e, acc)}
                        aria-label="Remove from list"
                        data-testid={`switcher-remove-${acc.user_id}`}
                        className="p-2 hover:opacity-60"
                      >
                        <X size={14} className="text-muted-foreground" />
                      </button>
                      <LogIn size={14} className="text-muted-foreground" />
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <Button
          onClick={addAccount}
          variant="outline"
          data-testid="switcher-add-account"
          className="rounded-none uppercase tracking-[0.2em] text-[11px] h-11 mt-4"
        >
          <Plus size={14} className="mr-2" />
          Add another account
        </Button>

        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          Switching will sign you out of this account and pre-fill the sign-in screen.
          You can return to this account anytime — it stays in this list.
        </p>
      </DialogContent>
    </Dialog>
  );
}
